/**
 * Máquina de estados do apontamento de uma ficha em um posto de trabalho.
 *
 * O tablet do posto só grava EVENTOS (append-only): quem, o quê, quando.
 * Estado atual, tempos e quantidades são sempre derivados dos eventos
 * (`reduzirEventos`). Isso dá:
 *  - auditoria completa para o fechamento mensal assinado pelo operador;
 *  - tolerância a offline (eventos ficam na fila do tablet e são reenviados);
 *  - zero chance de "tempo trabalhado" divergir do histórico.
 */
export const ESTADOS_FICHA = [
    'aguardando',
    'em_preset',
    'em_execucao',
    'pausada',
    'concluida',
    'cancelada',
];
export const TIPOS_EVENTO = [
    'iniciar_preset',
    'iniciar',
    'pausar',
    'retomar',
    'apontar',
    'concluir',
    'cancelar',
];
export class TransicaoInvalidaError extends Error {
    estado;
    evento;
    constructor(estado, evento) {
        super(`Evento "${evento}" não é permitido no estado "${estado}"`);
        this.estado = estado;
        this.evento = evento;
        this.name = 'TransicaoInvalidaError';
    }
}
export class EventoInvalidoError extends Error {
    constructor(mensagem) {
        super(mensagem);
        this.name = 'EventoInvalidoError';
    }
}
const TRANSICOES = {
    aguardando: {
        iniciar_preset: 'em_preset',
        iniciar: 'em_execucao',
        cancelar: 'cancelada',
    },
    em_preset: {
        iniciar: 'em_execucao',
        cancelar: 'cancelada',
    },
    em_execucao: {
        pausar: 'pausada',
        apontar: 'em_execucao',
        concluir: 'concluida',
        cancelar: 'cancelada',
    },
    pausada: {
        retomar: 'em_execucao',
        // Operador pode encerrar direto da pausa (ex.: esqueceu de retomar).
        concluir: 'concluida',
        cancelar: 'cancelada',
    },
    concluida: {},
    cancelada: {},
};
export const ESTADOS_FINAIS = new Set(['concluida', 'cancelada']);
export function proximoEstado(estado, evento) {
    const proximo = TRANSICOES[estado][evento];
    if (!proximo)
        throw new TransicaoInvalidaError(estado, evento);
    return proximo;
}
export function eventosPermitidos(estado) {
    return Object.keys(TRANSICOES[estado]);
}
function segundosEntre(inicio, fim) {
    return Math.max(0, (fim.getTime() - inicio.getTime()) / 1000);
}
/**
 * Reconstrói o estado e os tempos de uma ficha a partir de seus eventos
 * (em ordem cronológica). `agora` fecha o intervalo aberto de fichas ainda em
 * andamento — para a tela do posto e o painel em tempo real.
 */
export function reduzirEventos(eventos, agora = new Date()) {
    let estado = 'aguardando';
    let tempoPresetSeg = 0;
    let tempoExecucaoSeg = 0;
    const pausasPorMotivoSeg = {};
    let quantidadeApontada = 0;
    let iniciadoEm = null;
    let concluidoEm = null;
    let marcoEstado = null;
    let motivoPausaAtual = null;
    let ultimoEm = null;
    const fecharIntervalo = (ate) => {
        if (!marcoEstado)
            return;
        const seg = segundosEntre(marcoEstado, ate);
        if (estado === 'em_preset')
            tempoPresetSeg += seg;
        else if (estado === 'em_execucao')
            tempoExecucaoSeg += seg;
        else if (estado === 'pausada' && motivoPausaAtual) {
            pausasPorMotivoSeg[motivoPausaAtual] = (pausasPorMotivoSeg[motivoPausaAtual] ?? 0) + seg;
        }
    };
    for (const evento of eventos) {
        if (ultimoEm && evento.em.getTime() < ultimoEm.getTime()) {
            throw new EventoInvalidoError('Eventos devem estar em ordem cronológica');
        }
        ultimoEm = evento.em;
        if (evento.tipo === 'pausar' && !evento.motivo) {
            throw new EventoInvalidoError('Pausa exige um motivo');
        }
        if ((evento.tipo === 'apontar' || evento.tipo === 'concluir') && evento.quantidade != null) {
            if (!Number.isInteger(evento.quantidade) || evento.quantidade < 0) {
                throw new EventoInvalidoError('Quantidade apontada deve ser um inteiro não negativo');
            }
        }
        if (evento.tipo === 'apontar' && evento.quantidade == null) {
            throw new EventoInvalidoError('Apontamento exige quantidade');
        }
        const novoEstado = proximoEstado(estado, evento.tipo);
        fecharIntervalo(evento.em);
        if (evento.tipo === 'iniciar' && !iniciadoEm)
            iniciadoEm = evento.em;
        if (evento.tipo === 'pausar')
            motivoPausaAtual = evento.motivo ?? null;
        if (evento.tipo === 'retomar')
            motivoPausaAtual = null;
        if (evento.quantidade != null)
            quantidadeApontada += evento.quantidade;
        if (novoEstado === 'concluida')
            concluidoEm = evento.em;
        estado = novoEstado;
        marcoEstado = ESTADOS_FINAIS.has(estado) ? null : evento.em;
    }
    // Ficha em andamento: conta o tempo até "agora".
    if (marcoEstado)
        fecharIntervalo(agora);
    const tempoPausadoSeg = Object.values(pausasPorMotivoSeg).reduce((a, b) => a + b, 0);
    return {
        estado,
        tempoPresetSeg,
        tempoExecucaoSeg,
        pausasPorMotivoSeg,
        tempoPausadoSeg,
        quantidadeApontada,
        iniciadoEm,
        concluidoEm,
    };
}
//# sourceMappingURL=maquina-estados.js.map